import {
  FIELD_SIZES,
  LANE_COUNT,
  getRoundCount,
  getRoundName,
  isValidFieldSize,
  type BotDifficulty,
  type PlayerInfo,
} from '@sprinters/shared';
import { unlockAudio } from '../../game/audio';
import { errorMessage } from '../../i18n/fr';
import type { GameClient } from '../../net/GameClient';
import type { AppState } from '../../state/store';
import { h, replaceChildren } from '../dom';
import type { Notify, Screen } from './types';

const DIFFICULTY_LABELS: Readonly<Record<BotDifficulty, string>> = {
  easy: 'Débutants',
  normal: 'Moyens',
  hard: 'Rapides',
};

export class LobbyScreen implements Screen {
  readonly element: HTMLElement;

  private readonly codeButton: HTMLButtonElement;
  private readonly playersList: HTMLElement;
  private readonly playersTitle: HTMLElement;
  private readonly fieldSelect: HTMLSelectElement;
  private readonly difficultySelect: HTMLSelectElement;
  private readonly summary: HTMLElement;
  private readonly startButton: HTMLButtonElement;
  private readonly hostHint: HTMLElement;
  private code = '';

  constructor(
    private readonly client: GameClient,
    private readonly notify: Notify,
  ) {
    this.codeButton = h('button', {
      class: 'room-code',
      type: 'button',
      title: 'Copier le code',
      onClick: () => void this.copy(this.code, 'Code copié !'),
    });
    this.playersList = h('ul', { class: 'players' });
    this.playersTitle = h('h2', null, 'Joueurs');

    this.fieldSelect = h(
      'select',
      { id: 'field-size', class: 'input', onChange: () => this.pushSettings() },
      ...FIELD_SIZES.map((size) =>
        h(
          'option',
          { value: size },
          size === LANE_COUNT ? '8 coureurs (finale directe)' : `${size} coureurs`,
        ),
      ),
    );
    this.difficultySelect = h(
      'select',
      { id: 'bot-difficulty', class: 'input', onChange: () => this.pushSettings() },
      ...(Object.keys(DIFFICULTY_LABELS) as BotDifficulty[]).map((key) =>
        h('option', { value: key }, DIFFICULTY_LABELS[key]),
      ),
    );
    this.summary = h('p', { class: 'muted' });
    this.startButton = h(
      'button',
      { class: 'btn btn-primary btn-lg', type: 'button', onClick: () => void this.start() },
      'Lancer le tournoi',
    );
    this.hostHint = h('p', { class: 'muted center' });

    this.element = h(
      'main',
      { class: 'lobby' },
      h(
        'header',
        { class: 'lobby-header' },
        h('div', null, h('p', { class: 'label' }, 'Code du salon'), this.codeButton),
        h(
          'div',
          { class: 'lobby-actions' },
          h(
            'button',
            {
              class: 'btn btn-secondary',
              type: 'button',
              onClick: () => void this.copy(this.inviteLink(), 'Lien copié !'),
            },
            'Copier le lien',
          ),
          h(
            'button',
            { class: 'btn btn-ghost', type: 'button', onClick: () => this.client.leaveRoom() },
            'Quitter',
          ),
        ),
      ),
      h(
        'div',
        { class: 'lobby-grid' },
        h('section', { class: 'card' }, this.playersTitle, this.playersList),
        h(
          'section',
          { class: 'card' },
          h('h2', null, 'Tournoi'),
          h('label', { class: 'label', for: 'field-size' }, 'Nombre de coureurs (joueurs + bots)'),
          this.fieldSelect,
          h('label', { class: 'label', for: 'bot-difficulty' }, 'Niveau des bots'),
          this.difficultySelect,
          this.summary,
          this.startButton,
          this.hostHint,
        ),
      ),
    );
  }

  update(state: AppState): void {
    const { room, session } = state;
    if (!room || !session) return;

    this.code = room.code;
    this.codeButton.textContent = room.code;
    const isHost = room.hostId === session.playerId;
    const players = room.players.filter((player) => player.role === 'player');
    const spectators = room.players.length - players.length;

    this.playersTitle.textContent = `Joueurs (${players.length}/${room.settings.fieldSize})`;
    replaceChildren(
      this.playersList,
      ...room.players.map((player) => this.renderPlayer(player, session.playerId, isHost)),
    );

    if (this.fieldSelect.value !== String(room.settings.fieldSize)) {
      this.fieldSelect.value = String(room.settings.fieldSize);
    }
    if (this.difficultySelect.value !== room.settings.botDifficulty) {
      this.difficultySelect.value = room.settings.botDifficulty;
    }
    this.fieldSelect.disabled = !isHost;
    this.difficultySelect.disabled = !isHost;

    const { fieldSize } = room.settings;
    const bots = Math.max(0, fieldSize - players.length);
    this.summary.textContent =
      `${players.length} joueur${players.length > 1 ? 's' : ''} + ${bots} bot${bots > 1 ? 's' : ''} · ` +
      `${getRoundCount(fieldSize)} tour${getRoundCount(fieldSize) > 1 ? 's' : ''}, départ en « ${getRoundName(fieldSize)} »` +
      (spectators > 0 ? ` · ${spectators} spectateur${spectators > 1 ? 's' : ''}` : '');

    this.startButton.hidden = !isHost;
    this.startButton.disabled = players.length === 0;
    this.hostHint.textContent = isHost
      ? 'Les joueurs sont répartis au hasard entre les séries de 8.'
      : "En attente du lancement par l'hôte…";
  }

  destroy(): void {
    // Rien à libérer.
  }

  private renderPlayer(player: PlayerInfo, myId: string, iAmHost: boolean): HTMLElement {
    const canKick = iAmHost && player.id !== myId;
    return h(
      'li',
      { class: `player${player.connected ? '' : ' player-offline'}` },
      h(
        'span',
        { class: 'avatar', 'aria-hidden': 'true' },
        player.nickname.slice(0, 1).toUpperCase(),
      ),
      h('span', { class: 'player-name' }, player.nickname),
      player.isHost && h('span', { class: 'badge badge-host' }, 'Hôte'),
      player.id === myId && h('span', { class: 'badge' }, 'Vous'),
      player.role === 'spectator' && h('span', { class: 'badge badge-muted' }, 'Spectateur'),
      !player.connected && h('span', { class: 'badge badge-muted' }, 'Hors ligne'),
      canKick &&
        h(
          'button',
          {
            class: 'btn btn-ghost btn-sm',
            type: 'button',
            title: `Exclure ${player.nickname}`,
            onClick: () => void this.kick(player),
          },
          'Exclure',
        ),
    );
  }

  private pushSettings(): void {
    const fieldSize = Number(this.fieldSelect.value);
    if (!isValidFieldSize(fieldSize)) return;
    void this.client
      .configure({ fieldSize, botDifficulty: this.difficultySelect.value as BotDifficulty })
      .then((result) => {
        if (!result.ok) this.notify(errorMessage(result.error), 'error');
      });
  }

  private async start(): Promise<void> {
    unlockAudio();
    const result = await this.client.startTournament();
    if (!result.ok) this.notify(errorMessage(result.error), 'error');
  }

  private async kick(player: PlayerInfo): Promise<void> {
    const result = await this.client.kick(player.id);
    if (!result.ok) this.notify(errorMessage(result.error), 'error');
  }

  private inviteLink(): string {
    return `${location.origin}${location.pathname}?room=${this.code}`;
  }

  private async copy(text: string, message: string): Promise<void> {
    try {
      await navigator.clipboard.writeText(text);
      this.notify(message);
    } catch {
      this.notify(`Copiez manuellement : ${text}`, 'error');
    }
  }
}
